/**
 * Subscription Slice
 * Manages corporate subscription tier and feature access
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import djangoClient, { parseDjangoError } from '../../shared/api/djangoClient';

// Subscription tiers
export const SUBSCRIPTION_TIERS = {
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise',
};

// Feature flags per tier
export const TIER_FEATURES = {
  [SUBSCRIPTION_TIERS.BASIC]: [
    'risk:basic',
    'compliance:basic',
    'audit:basic',
    'policy:read',
    'dashboard:basic',
  ],
  [SUBSCRIPTION_TIERS.PROFESSIONAL]: [
    'risk:advanced',
    'compliance:advanced',
    'audit:advanced',
    'policy:full',
    'dashboard:advanced',
    'ai:basic',
    'integration:basic',
    'report:advanced',
  ],
  [SUBSCRIPTION_TIERS.ENTERPRISE]: [
    'risk:enterprise',
    'compliance:enterprise',
    'audit:enterprise',
    'policy:enterprise',
    'dashboard:enterprise',
    'ai:advanced',
    'integration:advanced',
    'report:enterprise',
    'blockchain:enabled',
    'governance:full',
    'ethics:full',
    'it_governance:full',
    'ai_governance:full',
    'custom:branding',
    'custom:workflows',
    'support:priority',
  ],
};

const initialState = {
  tier: SUBSCRIPTION_TIERS.BASIC,
  features: [],
  limits: {
    users: 5,
    risks: 100,
    audits: 10,
    policies: 20,
    storage_gb: 5,
  },
  usage: {
    users: 0,
    risks: 0,
    audits: 0,
    policies: 0,
    storage_gb: 0,
  },
  billingCycle: 'monthly', // monthly, annual
  expiryDate: null,
  isActive: true,
  // PLT-06: `tier` defaults to 'basic', so consumers that gate on it must know whether it is
  // the real value. False while a fetch is pending, true once it has resolved.
  loaded: false,
  isLoading: false,
  error: null,
};

/**
 * Async thunk: Fetch subscription details
 */
export const fetchSubscription = createAsyncThunk(
  'subscription/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await djangoClient.get('/api/v1/auth/subscription/');
      return response.data;
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

// IDN-18: `fetchUsage` (GET /api/v1/auth/subscription/usage/) and
// `upgradeSubscription` (POST /api/v1/auth/subscription/upgrade/) used to
// live here, but neither backend route was ever implemented (only
// GET /api/v1/auth/subscription/ exists -- see
// apps/identity/urls.py/views.py::subscription_view) and no component ever
// dispatched either thunk or read `selectUsage`. Removed as dead code
// pointing at phantom endpoints rather than left to silently 404 if ever
// wired up. Real usage tracking + an upgrade/billing flow is a genuine
// unbuilt feature (needs a payment-provider decision) -- out of scope for
// this fix; see docs/remediation/ledger/M-IDN.md IDN-18 DECISION row.

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setSubscriptionData: (state, action) => {
      Object.assign(state, action.payload);
    },
    updateUsage: (state, action) => {
      state.usage = { ...state.usage, ...action.payload };
    },
    clearSubscriptionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch subscription
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.isLoading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loaded = true;
        state.tier = action.payload.tier;
        state.features = action.payload.features;
        state.limits = action.payload.limits;
        state.billingCycle = action.payload.billing_cycle;
        state.expiryDate = action.payload.expiry_date;
        state.isActive = action.payload.is_active;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
    // IDN-18: fetchUsage/upgradeSubscription extraReducers cases removed
    // alongside the thunks themselves (see comment above) -- neither
    // backend route existed.
  },
});

// Selectors
export const selectSubscriptionTier = (state) => state.subscription.tier;
export const selectSubscriptionLoaded = (state) => Boolean(state.subscription?.loaded);
export const selectFeatures = (state) => state.subscription.features;
export const selectLimits = (state) => state.subscription.limits;
export const selectUsage = (state) => state.subscription.usage;
export const selectIsSubscriptionActive = (state) => state.subscription.isActive;
export const selectSubscriptionLoading = (state) => state.subscription.isLoading;
export const selectSubscriptionError = (state) => state.subscription.error;

// Check if feature is available in current subscription
export const selectHasFeature = (state, feature) => {
  return state.subscription.features.includes(feature);
};

// Check if limit is reached
export const selectIsLimitReached = (state, limitType) => {
  return state.subscription.usage[limitType] >= state.subscription.limits[limitType];
};

// Get usage percentage for a limit
export const selectUsagePercentage = (state, limitType) => {
  const usage = state.subscription.usage[limitType] || 0;
  const limit = state.subscription.limits[limitType] || 1;
  return Math.round((usage / limit) * 100);
};

export const { setSubscriptionData, updateUsage, clearSubscriptionError } =
  subscriptionSlice.actions;

export default subscriptionSlice.reducer;
